import type { ReactNode } from 'react';

import { useOnboardingContext } from '@/features/onboarding-v2/context/OnboardingContext';
import { useLocalizedSpecialtyName } from '@/features/onboarding-v2/hooks/useLocalizedSpecialtyName';
import { useSelectedEmrOption } from '@/features/onboarding-v2/hooks/useSelectedEmrOption';
import { useSelectedSpecialtyCanonicalName } from '@/features/onboarding-v2/hooks/useSelectedSpecialtyCanonicalName';
import {
  ForestPatternBackground,
  GhostBadgeCardSvg,
  PreviewPanelVisualTransition,
  resolvePreviewLayout,
  SkyPatternBackground,
  SunlightPatternBackground,
} from '@heidi/growth';
import { OnboardingPhotoBackground } from '../../OnboardingPhotoBackground';
import { CompletionPreview } from './CompletionPreview';
import { DocumentSelectionPreview } from './DocumentSelectionPreview';
import { InviteTeamPreview } from './InviteTeamPreview';
import { MicPermissionsPreview } from './MicPermissionsPreview';
import { MicTestPreview } from './MicTestPreview';
import { PersonalizationBadgeCard } from './PersonalizationBadgeCard';
import {
  LoadingAnimationContent,
  TemplateReviewContent,
  TemplatesListContent,
} from './PreviewPanelContent';
import { getPreviewInnerVisualKey } from './previewPanelVisualKey';

function PreviewPanelShell({
  background,
  children,
  roundedClassName = 'rounded-2xl',
}: {
  background: ReactNode;
  children: ReactNode;
  roundedClassName?: string;
}) {
  return (
    <div className={`relative h-full overflow-hidden ${roundedClassName}`}>
      {background}
      <div className="relative h-full">{children}</div>
    </div>
  );
}

export function PreviewPanel() {
  const { formData, currentStep, template, documents, micTest, isGuidedTour } =
    useOnboardingContext();
  const firstName = formData.firstName;
  const lastName = formData.lastName;
  const clinicName = formData.clinicName;
  const specialtyName = useLocalizedSpecialtyName();
  const specialtyCanonicalName = useSelectedSpecialtyCanonicalName();
  const emrEhr = formData.emrEhr;
  const clinicSize = formData.clinicSize;
  const selectedEmr = useSelectedEmrOption(emrEhr, formData.country?.countryCode);

  const visualKey =
    getPreviewInnerVisualKey({ currentStep, emrEhr, clinicSize }) ?? currentStep;

  const layout = resolvePreviewLayout(currentStep, formData);

  const renderTemplateFlowInner = (
    innerStep: 'template-creation' | 'template-loading' | 'template-review'
  ) => {
    switch (innerStep) {
      case 'template-creation':
        return <TemplatesListContent />;
      case 'template-loading':
        return <LoadingAnimationContent />;
      case 'template-review':
        return (
          <TemplateReviewContent
            templateName={template.generatedTemplateDisplayName}
            templateContent={template.generatedTemplateDisplayContent}
            specialtyName={specialtyCanonicalName}
            isEditable={template.isEditingTemplate}
            isApplyingChanges={template.isEditing}
            onContentChange={template.handleTemplateContentChange}
            onTemplateNameChange={template.handleTemplateNameChange}
          />
        );
      default:
        return null;
    }
  };

  if (!layout) {
    return null;
  }

  if (layout.kind === 'completion') {
    return (
      <PreviewPanelShell
        background={
          isGuidedTour ? (
            <OnboardingPhotoBackground photo="sunshine" />
          ) : (
            <SunlightPatternBackground />
          )
        }
      >
        <PreviewPanelVisualTransition visualKey={visualKey}>
          <CompletionPreview
            countryCode={formData.country?.countryCode}
            isGuidedTour={isGuidedTour}
          />
        </PreviewPanelVisualTransition>
      </PreviewPanelShell>
    );
  }

  if (layout.kind === 'document-selection') {
    return (
      <PreviewPanelShell background={<ForestPatternBackground />}>
        <PreviewPanelVisualTransition visualKey={visualKey}>
          <DocumentSelectionPreview
            selectedTemplates={documents.selectedTemplates}
            createdTemplateName={template.createdTemplateName}
          />
        </PreviewPanelVisualTransition>
      </PreviewPanelShell>
    );
  }

  if (layout.kind === 'mic-permissions') {
    return (
      <PreviewPanelShell background={<ForestPatternBackground />}>
        <PreviewPanelVisualTransition visualKey={visualKey}>
          <MicPermissionsPreview />
        </PreviewPanelVisualTransition>
      </PreviewPanelShell>
    );
  }

  if (layout.kind === 'mic-test') {
    // The guided-tour frames float the note card inside the same rounded pane
    // every other tour step uses, over a photo instead of the tinted field.
    if (isGuidedTour) {
      const isEvidenceStep = currentStep === 'tour-evidence';
      return (
        <PreviewPanelShell
          background={
            // The evidence frame warms the backdrop; everything up to it stays
            // on the blossom photo the transcription frame introduces.
            <OnboardingPhotoBackground photo={isEvidenceStep ? 'sunshine' : 'blossom'} />
          }
        >
          <MicTestPreview
            phase={micTest.phase}
            transcript={micTest.transcript}
            onPhaseChange={micTest.setPhase}
            onEditActivity={micTest.setEditActivity}
            recordingDurationSec={micTest.recordingDurationSec}
            isGuidedTour
            isTourEvidenceStep={isEvidenceStep}
          />
        </PreviewPanelShell>
      );
    }
    return (
      <div className="-my-6 -mr-6 h-dvh">
        <MicTestPreview
          phase={micTest.phase}
          transcript={micTest.transcript}
          onPhaseChange={micTest.setPhase}
          onEditActivity={micTest.setEditActivity}
          recordingDurationSec={micTest.recordingDurationSec}
        />
      </div>
    );
  }

  if (layout.kind === 'template-flow') {
    return (
      <div className="relative h-full overflow-hidden rounded-lg">
        <ForestPatternBackground />
        <div className="relative h-full">
          <PreviewPanelVisualTransition visualKey={visualKey}>
            {renderTemplateFlowInner(layout.innerStep)}
          </PreviewPanelVisualTransition>
        </div>
      </div>
    );
  }

  if (layout.kind === 'clinic-grid') {
    // The guided-tour clinic frame drops the ghost cards behind the badge and
    // shows the single card on the photo, same as its create-account frame.
    if (isGuidedTour) {
      return (
        <PreviewPanelShell background={<OnboardingPhotoBackground photo="field" />}>
          <PreviewPanelVisualTransition visualKey={visualKey}>
            <div className="flex size-full items-center justify-center">
              <PersonalizationBadgeCard
                firstName={firstName}
                lastName={lastName}
                specialtyName={specialtyName}
                clinicName={clinicName}
                emrEhr={selectedEmr}
                displayLanguage={formData.displayLanguage}
                variant="forest"
              />
            </div>
          </PreviewPanelVisualTransition>
        </PreviewPanelShell>
      );
    }

    return (
      <PreviewPanelShell background={<SkyPatternBackground />}>
        <PreviewPanelVisualTransition visualKey={visualKey}>
          <div className="flex size-full items-center justify-center overflow-hidden">
            <div className="relative h-[456px] w-[657px]">
              <div className="absolute left-0 top-[96px]">
                <GhostBadgeCardSvg width={192} height={278} />
              </div>
              <div className="absolute left-[465px] top-[96px]">
                <GhostBadgeCardSvg width={192} height={278} />
              </div>
              <div className="absolute left-[70px] top-[52px]">
                <GhostBadgeCardSvg width={249} height={360} />
              </div>
              <div className="absolute left-[335px] top-[52px]">
                <GhostBadgeCardSvg width={249} height={360} />
              </div>
              <div className="absolute left-[171px] top-0">
                <PersonalizationBadgeCard
                  firstName={firstName}
                  lastName={lastName}
                  specialtyName={specialtyName}
                  clinicName={clinicName}
                  emrEhr={selectedEmr}
                  displayLanguage={formData.displayLanguage}
                />
              </div>
            </div>
          </div>
        </PreviewPanelVisualTransition>
      </PreviewPanelShell>
    );
  }

  if (layout.kind === 'invite-team') {
    return (
      <PreviewPanelShell
        background={
          isGuidedTour ? (
            <OnboardingPhotoBackground photo="meadow" />
          ) : (
            <ForestPatternBackground />
          )
        }
      >
        <PreviewPanelVisualTransition visualKey={visualKey}>
          <InviteTeamPreview
            clinicName={clinicName}
            inviteEmails={formData.inviteEmails}
          />
        </PreviewPanelVisualTransition>
      </PreviewPanelShell>
    );
  }

  if (layout.kind === 'badge-card') {
    return (
      <PreviewPanelShell
        background={
          isGuidedTour ? (
            <OnboardingPhotoBackground photo="field" />
          ) : (
            <SkyPatternBackground />
          )
        }
      >
        <PreviewPanelVisualTransition visualKey={visualKey}>
          <div className="flex size-full items-center justify-center">
            <PersonalizationBadgeCard
              firstName={firstName}
              lastName={lastName}
              specialtyName={layout.showSpecialty ? specialtyName : undefined}
              clinicName={layout.showClinic ? clinicName : undefined}
              emrEhr={layout.showClinic ? selectedEmr : undefined}
              displayLanguage={formData.displayLanguage}
              variant={isGuidedTour ? 'forest' : 'sand'}
            />
          </div>
        </PreviewPanelVisualTransition>
      </PreviewPanelShell>
    );
  }

  return null;
}
